import { Component, Inject, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { SuperAdminService } from '../services/super-admin.service';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from './confirmation-permanent-delete-dialog.component'
import { ConfirmDialogComponent } from './confirm-dialog.component'


export interface OrganizationDetailsDialogData {
  organizationId: string;
  isPending: boolean;
  approvalId?: string;
  organizationName?: string;
  isArchived?: boolean;
  approvalData?: any;
}

export interface OrganizationDetailsDialogResult {
  action: 'approve' | 'reject' | 'deactivate' | 'archive' | 'delete' | 'reactivate';
  success: boolean;
  shouldRefresh: boolean;
  organizationId?: string;  // Add this
  organizationData?: any;
}

type ActionType = 'approve' | 'reject' | 'deactivate' | 'archive' | 'delete' | 'reactivate';

@Component({
  selector: 'app-organization-details-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatCardModule,
    MatTooltipModule,
    MatSnackBarModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule
  ],
  template: `
    <div class="dialog-container">
      <div class="dialog-header">
        <h2 mat-dialog-title>
          <mat-icon>domain</mat-icon>
          Organization Details
        </h2>
        <button mat-icon-button (click)="onClose()" class="close-button">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-dialog-content>
        @if(loading) {
          <div class="loading-container">
            <mat-spinner diameter="50"></mat-spinner>
            <p>Loading organization details...</p>
          </div>
        } @else if(error) {
          <div class="error-container">
            <mat-icon color="warn">error</mat-icon>
            <p>{{ error }}</p>
          </div>
        } @else if(orgData) {
          <div class="details-content">

            <!-- Organization Name -->
            <div class="detail-section">
              <h3 class="section-title">
                <mat-icon>business</mat-icon>
                Organization Information
              </h3>
             <div class="detail-row">
                <span class="label">Name:</span>
                <span class="value">
                  {{ getName(orgData) }}
                  @if(isParentOrganization()) {
                    <span class="parent-org-badge" matTooltip="This is the main parent organization">
                      <mat-icon>star</mat-icon>
                      Parent Org
                    </span>
                  }
                </span>
              </div>
              @if(getOrgCode(orgData)) {
    <div class="detail-row">
      <span class="label">Organization Code:</span>
      <span class="value">
        <span class="badge badge-blue">{{ getOrgCode(orgData) }}</span>
      </span>
    </div>
  }
              <div class="detail-row">
                <span class="label">Type:</span>
                <span class="value">
                  <span class="badge badge-blue">{{ getType(orgData) }}</span>
                </span>
              </div>
              <div class="detail-row">
                <span class="label">Status:</span>
                <span class="value">
                  <span [class]="'badge ' + (isActive(orgData) ? 'badge-green' : 'badge-orange')">
                    <mat-icon>{{ isActive(orgData) ? 'check_circle' : 'pending' }}</mat-icon>
                    {{ isActive(orgData) ? 'Active' : 'Inactive' }}
                  </span>
                </span>
              </div>
            </div>

            <mat-divider></mat-divider>

            @if(getDescription(orgData)) {
              <div class="detail-row">
                <span class="label">Description:</span>
                <span class="value description-text">{{ getDescription(orgData) }}</span>
              </div>
            }

            @if (getQualifications(orgData).length > 0) {
              <div class="qualifications-section">
                <span class="label">Qualifications:</span>

                <div class="qualifications-list">
                  @for (qual of getQualifications(orgData); track $index) {
                    <div class="qualification-item">
                      @if (qual.description) {
                        <div class="qualification-code">
                          {{ qual.description }}
                        </div>
                      }

                      @if (qual.issuer) {
                        <div class="qualification-issuer">
                          <span class="issuer-label">Issuer:</span>
                          <span class="issuer-value">{{ qual.issuer }}</span>
                        </div>
                      }

                      @if (qual.website) {
                        <div class="qualification-reference">
                          <a [href]="qual.website" target="_blank" rel="noopener">
                            <mat-icon>link</mat-icon>
                            View Issuer
                          </a>
                        </div>
                      }
                    </div>
                  }
                </div>
              </div>
            }

            <mat-divider></mat-divider>

            <!-- Contact Information -->
            <div class="detail-section">
              <h3 class="section-title">
                <mat-icon>contact_phone</mat-icon>
                Contact Information
              </h3>
              @if(getPhones(orgData).length > 0) {
                @for(phone of getPhones(orgData); track $index) {
                  <div class="detail-row">
                    <span class="label">Phone {{ phone.use ? '(' + phone.use + ')' : '' }}:</span>
                    <span class="value">
                      <a href="tel:{{ phone.value }}">{{ phone.value }}</a>
                    </span>
                  </div>
                }
              }
              @if(getEmails(orgData).length > 0) {
                @for(email of getEmails(orgData); track $index) {
                  <div class="detail-row">
                    <span class="label">Email {{ email.use ? '(' + email.use + ')' : '' }}:</span>
                    <span class="value">
                      <a href="mailto:{{ email.value }}">{{ email.value }}</a>
                    </span>
                  </div>
                }
              }
            </div>

            <mat-divider></mat-divider>

            <!-- Address Information -->
            @if(getAddresses(orgData).length > 0) {
              <div class="detail-section">
                <h3 class="section-title">
                  <mat-icon>location_on</mat-icon>
                  Address
                </h3>
                @for(address of getAddresses(orgData); track $index) {
                  <div class="address-block">
                    @if(address.line && address.line.length > 0) {
                      @for(line of address.line; track $index) {
                        <div>{{ line }}</div>
                      }
                    }
                    <div>
                      {{ [address.city, address.state, address.postalCode].filter(Boolean).join(', ') }}
                    </div>
                    @if(address.country) {
                      <div>{{ address.country }}</div>
                    }
                  </div>
                }
              </div>
            }

            <!-- Approval Information -->
             @if(approvalData) {
              <mat-divider></mat-divider>
              <div class="detail-section">
                <h3 class="section-title">
                  <mat-icon>admin_panel_settings</mat-icon>
                  Administrator Information
                </h3>
                @if(approvalData.adminFirstName || approvalData.adminLastName) {
                  <div class="detail-row">
                    <span class="label">Name:</span>
                    <span class="value">{{ approvalData.adminFirstName }} {{ approvalData.adminLastName }}</span>
                  </div>
                }
                @if(approvalData.adminEmail) {
                  <div class="detail-row">
                    <span class="label">Email:</span>
                    <span class="value">
                      <a href="mailto:{{ approvalData.adminEmail }}">{{ approvalData.adminEmail }}</a>
                    </span>
                  </div>
                }
                @if(approvalData.adminPhone) {
                  <div class="detail-row">
                    <span class="label">Phone:</span>
                    <span class="value">{{ approvalData.adminPhone }}</span>
                  </div>
                }
                @if(approvalData.submittedAt) {
                  <div class="detail-row">
                    <span class="label">Submitted:</span>
                    <span class="value">{{ approvalData.submittedAt | date:'medium' }}</span>
                  </div>
                }
                @if(approvalData.reviewedAt && !data.isPending) {
                  <div class="detail-row">
                    <span class="label">Reviewed:</span>
                    <span class="value">{{ approvalData.reviewedAt | date:'medium' }}</span>
                  </div>
                }
              </div>
              }




          </div>
        }
      </mat-dialog-content>

      <mat-dialog-actions align="end">
  <!-- Rejection Flow -->
  @if(showRejectInput) {
    <button mat-button (click)="cancelReject()">Cancel</button>
    <button mat-flat-button color="warn"
            (click)="confirmReject()"
            [disabled]="processing || !rejectionReason.trim()">
      @if(processing) {
        <mat-spinner diameter="20"></mat-spinner>
      } @else {
        <mat-icon>block</mat-icon>
      }
      Confirm Reject
    </button>
  }

  <!-- Pending Organization Actions - ONLY Approve and Reject -->
  @else if(data.isPending && !loading && !error) {
    <button mat-flat-button color="warn" (click)="onReject()" [disabled]="processing">
      @if(processing && actionType === 'reject') {
        <mat-spinner diameter="20"></mat-spinner>
      } @else {
        <mat-icon>block</mat-icon>
      }
      Reject
    </button>
    <button mat-flat-button color="primary" (click)="onApprove()" [disabled]="processing">
      @if(processing && actionType === 'approve') {
        <mat-spinner diameter="20"></mat-spinner>
      } @else {
        <mat-icon>check</mat-icon>
      }
      Approve
    </button>
  }

  <!-- Archived Organization Actions - ONLY Reactivate and Delete (if not parent) -->
  @else if(isArchived() && !data.isPending && !loading && !error) {
    @if(!isParentOrganization()) {
      <button mat-button color="warn" (click)="onDelete()" [disabled]="processing">
        <mat-icon>delete_forever</mat-icon>
        Delete
      </button>
    }
    <button mat-flat-button color="primary" (click)="onReactivate()" [disabled]="processing">
      @if(processing && actionType === 'reactivate') {
        <mat-spinner diameter="20"></mat-spinner>
      } @else {
        <mat-icon>restart_alt</mat-icon>
      }
      Reactivate
    </button>
    <button mat-button (click)="onClose()">Close</button>
  }

  <!-- Active Non-Archived Organization Actions - Deactivate, Archive, Delete -->
  @else if(!data.isPending && !loading && !error && isActive(orgData) && !isArchived()) {
    @if(canModify()) {
      <button mat-button (click)="onDeactivate()" [disabled]="processing">
        <mat-icon>pause_circle</mat-icon>
        Deactivate
      </button>
      <button mat-button (click)="onArchive()" [disabled]="processing">
        <mat-icon>archive</mat-icon>
        Archive
      </button>
      <button mat-button color="warn" (click)="onDelete()" [disabled]="processing">
        <mat-icon>delete_forever</mat-icon>
        Delete
      </button>
    }
    <button mat-button (click)="onClose()">Close</button>
  }

  <!-- Deleted Organization Actions - ONLY Restore -->
@else if(isDeleted() && !data.isPending && !loading && !error) {
  <button mat-flat-button color="primary" (click)="onRestore()" [disabled]="processing">
    @if(processing && actionType === 'reactivate') {
      <mat-spinner diameter="20"></mat-spinner>
    } @else {
      <mat-icon>restore</mat-icon>
    }
    Restore
  </button>
  <button mat-button (click)="onClose()">Close</button>
}
  <!-- Inactive (but not archived) Organization Actions - Archive, Delete, Reactivate -->
  @else if(!data.isPending && !loading && !error && !isActive(orgData) && !isArchived()) {
    @if(!isParentOrganization()) {
      <button mat-button (click)="onArchive()" [disabled]="processing">
        <mat-icon>archive</mat-icon>
        Archive
      </button>
      <button mat-button color="warn" (click)="onDelete()" [disabled]="processing">
        <mat-icon>delete_forever</mat-icon>
        Delete
      </button>
    }
    <button mat-flat-button color="primary" (click)="onReactivate()" [disabled]="processing">
      @if(processing && actionType === 'reactivate') {
        <mat-spinner diameter="20"></mat-spinner>
      } @else {
        <mat-icon>restart_alt</mat-icon>
      }
      Reactivate
    </button>
    <button mat-button (click)="onClose()">Close</button>
  }

  <!-- Default Close for parent organizations or other cases -->
  @else {
    <button mat-button (click)="onClose()">Close</button>
  }
</mat-dialog-actions>
    </div>
  `,
  styles: [`
    .dialog-container {
      display: flex;
      flex-direction: column;
      max-height: 100%;
    }

    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 24px;
      border-bottom: 1px solid #e0e0e0;
    }

    .dialog-header h2 {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 0;
      font-size: 1.5rem;
    }

    .close-button {
      margin-right: -12px;
    }

    mat-dialog-content {
      padding: 24px;
      overflow-y: auto;
    }

    .loading-container, .error-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px;
      gap: 16px;
    }

    .error-container mat-icon {
      font-size: 48px;
      width: 48px;
      height: 48px;
    }

    .details-content {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    .detail-section {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .confirmation-section {
      background: #fff3e0;
      padding: 16px;
      border-radius: 8px;
      border-left: 4px solid #ff9800;
    }

    .confirmation-section.danger {
      background: #ffebee;
      border-left-color: #f44336;
    }

    .section-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 1.1rem;
      font-weight: 500;
      margin: 0 0 8px 0;
      color: #1976d2;
    }

    .section-title.warning {
      color: #ff9800;
    }

    .section-title.danger {
      color: #f44336;
    }

    .warning-text {
      margin: 8px 0;
      color: #666;
      font-size: 0.9rem;
    }

    .danger-warning {
      display: flex;
      gap: 12px;
      padding: 12px;
      background: #fff;
      border-radius: 4px;
      margin-bottom: 16px;
    }

    .danger-warning mat-icon {
      color: #f44336;
      font-size: 24px;
      width: 24px;
      height: 24px;
    }

    .danger-warning strong {
      color: #f44336;
    }

    .danger-warning p {
      margin: 4px 0 0 0;
      font-size: 0.9rem;
    }

    .detail-row {
      display: grid;
      grid-template-columns: 150px 1fr;
      gap: 16px;
      padding: 8px 0;
    }

    .label {
      font-weight: 500;
      color: #666;
    }

    .value {
      color: #333;
    }

    .value a {
      color: #1976d2;
      text-decoration: none;
    }

    .value a:hover {
      text-decoration: underline;
    }

    .badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 12px;
      border-radius: 16px;
      font-size: 0.875rem;
      font-weight: 500;
    }

    .badge mat-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
    }

    .parent-org-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 10px;
      background-color: #fff3e0;
      color: #f57c00;
      border-radius: 12px;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .parent-org-badge mat-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
    }

    .badge-blue {
      background-color: #e3f2fd;
      color: #1976d2;
    }

    .badge-green {
      background-color: #e8f5e9;
      color: #2e7d32;
    }

    .badge-orange {
      background-color: #fff3e0;
      color: #f57c00;
    }

    .description-text {
      white-space: pre-wrap;
      word-break: break-word;
      line-height: 1.5;
    }

    .qualifications-section {
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid #e0e0e0;
    }

    .qualifications-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
      margin-top: 8px;
    }

    .qualification-item {
      background: #f9f9f9;
      border-left: 4px solid #1976d2;
      padding: 12px;
      border-radius: 4px;
    }

    .qualification-code {
      font-weight: 600;
      color: #1976d2;
      margin-bottom: 6px;
    }

    .qualification-issuer {
      display: grid;
      grid-template-columns: 60px 1fr;
      gap: 8px;
      font-size: 0.9rem;
      margin-bottom: 6px;
    }

    .issuer-label {
      font-weight: 500;
      color: #666;
    }

    .issuer-value {
      color: #333;
    }

    .qualification-reference {
      margin-top: 6px;
    }

    .qualification-reference a {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      color: #1976d2;
      text-decoration: none;
      font-size: 0.85rem;
    }

    .qualification-reference a:hover {
      text-decoration: underline;
    }

    .qualification-reference mat-icon {
      font-size: 14px;
      width: 14px;
      height: 14px;
    }

    .address-block {
      background: #f5f5f5;
      padding: 12px;
      border-radius: 8px;
      line-height: 1.6;
    }

    mat-divider {
      margin: 16px 0;
    }

    mat-dialog-actions {
      padding: 16px 24px;
      border-top: 1px solid #e0e0e0;
      gap: 12px;
    }

    mat-dialog-actions button {
      position: relative;
    }

    mat-dialog-actions mat-spinner {
      margin-right: 8px;
    }

    .full-width {
      width: 100%;
    }
  `]
})
export class OrganizationDetailsDialogComponent {
  private service = inject(SuperAdminService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  loading = true;
  processing = false;
  actionType: ActionType | null = null;
  error: string | null = null;
  orgData: any = null;
  approvalData: any = null;

  // Rejection
  showRejectInput = false;
  rejectionReason = '';

  // Deactivation
  showDeactivateInput = false;
  deactivationReason = '';

  // Archive
  showArchiveInput = false;
  archiveReason = '';

  // Delete
  showDeleteInput = false;
  deleteConfirmation = '';

  protected readonly Boolean = Boolean;

  constructor(
    public dialogRef: MatDialogRef<OrganizationDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OrganizationDetailsDialogData
  ) {
    this.loadOrganizationDetails();
  }

  private loadOrganizationDetails() {
    this.loading = true;
    this.error = null;

    this.service.getOrganizationById(this.data.organizationId).subscribe({
      next: (org: any) => {
        console.log('Organization details:', org);
        this.orgData = org;

        if (this.data.approvalData) {
          this.approvalData = this.data.approvalData;
          this.loading = false;
        } else if (this.data.isPending && this.data.approvalId) {
          this.loadApprovalData();
        } else {
          // Attempt to extract approval/admin data from various possible response structures
          this.approvalData = org.approval ||
            org.rawFhir?.approval ||
            org.raw?.approval ||
            org.RawFhir?.approval ||
            (org.extension?.find((ext: any) => ext.url === 'http://elikita.org/fhir/StructureDefinition/organization-approval')?.valueReference);
          this.loading = false;
        }
      },
      error: (err: any) => {
        console.error('Error loading organization:', err);
        this.error = 'Failed to load organization details';
        this.loading = false;
      }
    });
  }

  private loadApprovalData() {
    this.service.getPendingApprovals().subscribe({
      next: (approvals) => {
        this.approvalData = approvals.find(a => a.id === this.data.approvalId);
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading approval data:', err);
        this.loading = false;
      }
    });
  }

  // Helper methods
  private extractValue(obj: any): any {
    return obj?.value !== undefined ? obj.value : obj;
  }

  getName(org: any): string {
    return this.extractValue(org.name) || 'Unknown Organization';
  }

  getType(org: any): string {
    if (org.type && org.type.length > 0) {
      if (org.type[0].text) return this.extractValue(org.type[0].text);
      if (org.type[0].coding?.[0]?.display) return this.extractValue(org.type[0].coding[0].display);
      if (org.type[0].coding?.[0]?.code) return this.extractValue(org.type[0].coding[0].code);
    }
    return 'Healthcare Provider';
  }

  isActive(org: any): boolean {
    return this.extractValue(org.active) === true;
  }

  isParentOrganization(): boolean {
    if (!this.orgData) return false;
    return this.orgData?.extension?.some((ext: any) => {
      const isParentExtension = ext.url === 'http://elikita.org/fhir/StructureDefinition/is-parent-organization';
      if (!isParentExtension) return false;
      let boolValue = false;
      if (ext.valueBoolean !== undefined) {
        boolValue = ext.valueBoolean === true || ext.valueBoolean?.value === true;
      } else if (ext.value !== undefined) {
        boolValue = ext.value === true || ext.value?.value === true;
      }
      return boolValue;
    }) ?? false;
  }

  getPhones(org: any): any[] {
    if (!org.telecom || !Array.isArray(org.telecom)) return [];
    return org.telecom
      .filter((t: any) => this.extractValue(t.system) === 'phone')
      .map((t: any) => ({
        value: this.extractValue(t.value),
        use: this.extractValue(t.use)
      }));
  }

  getEmails(org: any): any[] {
    if (!org.telecom || !Array.isArray(org.telecom)) return [];
    return org.telecom
      .filter((t: any) => this.extractValue(t.system) === 'email')
      .map((t: any) => ({
        value: this.extractValue(t.value),
        use: this.extractValue(t.use)
      }));
  }
getOrgCode(org: any): string {
  if (!org?.meta?.tag || !Array.isArray(org.meta.tag)) return '';
  
  const orgCodeTag = org.meta.tag.find(
    (tag: any) => this.extractValue(tag.system) === 'https://elikita-server.daalitech.com/CodeSystem/organizationCode'
  );
  
  return orgCodeTag ? this.extractValue(orgCodeTag.code) : '';
}
  getDescription(org: any): string {
    if (!org?.extension || !Array.isArray(org.extension)) return '';
    const ext = org.extension.find(
      (e: any) => e.url === 'http://elikita.org/fhir/StructureDefinition/organization-description'
    );
    return ext?.valueString ?? ext?.value?.value ?? '';
  }

  getQualifications(org: any): any[] {
    if (!Array.isArray(org?.extension)) return [];
    const qualificationExts = org.extension.filter(
      (e: any) => e.url === 'http://localhost:5289/fhir/organization-qualification'
    );
    return qualificationExts.map((qExt: any) => {
      const sub = (url: string) =>
        qExt.extension?.find((e: any) => e.url === url)?.value?.value ?? '';
      return {
        description: sub('description'),
        issuer: sub('issuer'),
        website: sub('website')
      };
    }).filter((q: any) => q.description || q.issuer || q.website);
  }

  getAddresses(org: any): any[] {
    if (!org.address || !Array.isArray(org.address)) return [];
    return org.address.map((a: any) => ({
      line: a.line?.map((l: any) => this.extractValue(l)) || [],
      city: this.extractValue(a.city),
      state: this.extractValue(a.state),
      postalCode: this.extractValue(a.postalCode),
      country: this.extractValue(a.country)
    }));
  }

  onApprove() {
    if (!this.data.approvalId) return;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      data: {
        title: 'Approve Organization',
        message: `Are you sure you want to approve <strong>${this.data.organizationName || this.getName(this.orgData)}</strong>?`,
        details: [
          'Activate the organization in the system',
          `Create an admin account for ${this.approvalData?.adminFirstName || ''} ${this.approvalData?.adminLastName || ''}`,
          `Send login credentials to ${this.approvalData?.adminEmail || ''}`
        ],
        confirmText: 'Approve',
        cancelText: 'Cancel',
        confirmColor: 'primary'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.performApproval();
      }
    });
  }

  private performApproval() {
    this.processing = true;
    this.actionType = 'approve';

    this.service.approveOrganization(this.data.approvalId!).subscribe({
      next: () => {
        this.snackBar.open('Organization approved successfully', 'Close', {
          duration: 3000,
          horizontalPosition: 'end',
          verticalPosition: 'top'
        });
        this.dialogRef.close({
          action: 'approve',
          success: true,
          shouldRefresh: true,
          organizationId: this.data.organizationId,
          organizationData: this.orgData
        } as OrganizationDetailsDialogResult);
      },
      error: (err) => {
        console.error('Error approving organization:', err);
        this.snackBar.open('Failed to approve organization', 'Close', {
          duration: 5000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        this.processing = false;
        this.actionType = null;
      }
    });
  }


  onReject() {
    if (!this.data.approvalId) return;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '500px',
      data: {
        title: 'Confirm Rejection',
        message: `Are you sure you want to reject ${this.data.organizationName || this.getName(this.orgData)}?`,
        confirmText: 'Reject',
        cancelText: 'Cancel',
        isDanger: true,
        showReasonField: true,
        reasonLabel: 'Rejection Reason',
        reasonRequired: true
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirmed && result.reason) {
        this.performRejection(result.reason);
      }
    });
  }

  cancelReject() {
    this.showRejectInput = false;
    this.rejectionReason = '';
  }

  confirmReject() {
    if (!this.data.approvalId || !this.rejectionReason.trim()) return;
    this.processing = true;
    this.actionType = 'reject';

    this.service.rejectOrganization(this.data.approvalId, this.rejectionReason.trim()).subscribe({
      next: () => {
        this.snackBar.open('Organization rejected', 'Close', {
          duration: 3000,
          horizontalPosition: 'end',
          verticalPosition: 'top'
        });
        this.dialogRef.close({
          action: 'reject',
          success: true,
          shouldRefresh: true
        } as OrganizationDetailsDialogResult);
      },
      error: (err) => {
        console.error('Error rejecting organization:', err);
        this.snackBar.open('Failed to reject organization', 'Close', {
          duration: 5000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        this.processing = false;
        this.actionType = null;
      }
    });
  }

  private performRejection(reason: string) {
    this.processing = true;
    this.actionType = 'reject';

    this.service.rejectOrganization(this.data.approvalId!, reason).subscribe({
      next: () => {
        this.snackBar.open('Organization rejected', 'Close', {
          duration: 3000,
          horizontalPosition: 'end',
          verticalPosition: 'top'
        });
        this.dialogRef.close({
          action: 'reject',
          success: true,
          shouldRefresh: true,
          organizationId: this.data.organizationId,
          organizationData: this.orgData
        } as OrganizationDetailsDialogResult);
      },
      error: (err) => {
        console.error('Error rejecting organization:', err);
        this.snackBar.open('Failed to reject organization', 'Close', {
          duration: 5000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        this.processing = false;
        this.actionType = null;
      }
    });
  }


  onDeactivate() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '500px',
      data: {
        title: 'Confirm Deactivation',
        message: 'This will temporarily disable the organization and lock all user accounts. Data will be retained and can be reactivated.',
        confirmText: 'Deactivate',
        cancelText: 'Cancel',
        isDanger: false,
        showReasonField: true,
        reasonLabel: 'Deactivation Reason (Optional)',
        reasonRequired: false
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirmed) {
        this.processing = true;
        this.actionType = 'deactivate';

        this.service.deactivateOrganization(
          this.data.organizationId,
          result.reason
        ).subscribe({
          next: () => {
            this.snackBar.open('Organization deactivated successfully', 'Close', {
              duration: 3000,
              horizontalPosition: 'end',
              verticalPosition: 'top'
            });
            this.dialogRef.close({
              action: 'deactivate',
              success: true,
              shouldRefresh: true
            } as OrganizationDetailsDialogResult);
          },
          error: (err) => {
            console.error('Error deactivating organization:', err);
            this.snackBar.open('Failed to deactivate organization', 'Close', {
              duration: 5000,
              horizontalPosition: 'end',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            });
            this.processing = false;
            this.actionType = null;
          }
        });
      }
    });
  }

  onArchive() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '500px',
      data: {
        title: 'Confirm Archive',
        message: 'This will move the organization to archive status. All data will be retained and can be restored later.',
        confirmText: 'Archive',
        cancelText: 'Cancel',
        isDanger: false,
        showReasonField: true,
        reasonLabel: 'Archive Reason (Optional)',
        reasonRequired: false
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirmed) {
        this.processing = true;
        this.actionType = 'archive';

        this.service.deactivateOrganization(
          this.data.organizationId,
          result.reason
        ).subscribe({
          next: () => {
            this.snackBar.open('Organization archived successfully', 'Close', {
              duration: 3000,
              horizontalPosition: 'end',
              verticalPosition: 'top'
            });
            this.dialogRef.close({
              action: 'archive',
              success: true,
              shouldRefresh: true
            } as OrganizationDetailsDialogResult);
          },
          error: (err) => {
            console.error('Error archiving organization:', err);
            this.snackBar.open('Failed to archive organization', 'Close', {
              duration: 5000,
              horizontalPosition: 'end',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            });
            this.processing = false;
            this.actionType = null;
          }
        });
      }
    });
  }

  onDelete() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '500px',
      data: {
        title: 'Confirm Permanent Deletion',
        message: 'All organization data, users, and related records will be permanently deleted. This action cannot be undone.',
        confirmText: 'Permanently Delete',
        cancelText: 'Cancel',
        isDanger: true,
        requireTextConfirmation: true,
        confirmationWord: 'DELETE'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirmed) {
        this.processing = true;
        this.actionType = 'delete';

        this.service.deleteOrganizationPermanently(this.data.organizationId).subscribe({
          next: () => {
            this.snackBar.open('Organization permanently deleted', 'Close', {
              duration: 3000,
              horizontalPosition: 'end',
              verticalPosition: 'top'
            });
            this.dialogRef.close({
              action: 'delete',
              success: true,
              shouldRefresh: true
            } as OrganizationDetailsDialogResult);
          },
          error: (err) => {
            console.error('Error deleting organization:', err);
            this.snackBar.open('Failed to delete organization', 'Close', {
              duration: 5000,
              horizontalPosition: 'end',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            });
            this.processing = false;
            this.actionType = null;
          }
        });
      }
    });
  }

  cancelDeactivate() {
    this.showDeactivateInput = false;
    this.deactivationReason = '';
  }

  confirmDeactivate() {
    this.processing = true;
    this.actionType = 'deactivate';

    this.service.deactivateOrganization(
      this.data.organizationId,
      this.deactivationReason.trim() || undefined
    ).subscribe({
      next: () => {
        this.snackBar.open('Organization deactivated successfully', 'Close', {
          duration: 3000,
          horizontalPosition: 'end',
          verticalPosition: 'top'
        });
        this.dialogRef.close({
          action: 'deactivate',
          success: true,
          shouldRefresh: true
        } as OrganizationDetailsDialogResult);
      },
      error: (err) => {
        console.error('Error deactivating organization:', err);
        this.snackBar.open('Failed to deactivate organization', 'Close', {
          duration: 5000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        this.processing = false;
        this.actionType = null;
      }
    });
  }



  cancelArchive() {
    this.showArchiveInput = false;
    this.archiveReason = '';
  }

  confirmArchive() {
    this.processing = true;
    this.actionType = 'archive';

    // Using deactivateOrganization with archive reason
    this.service.deactivateOrganization(
      this.data.organizationId,
      this.archiveReason.trim() || undefined
    ).subscribe({
      next: () => {
        this.snackBar.open('Organization archived successfully', 'Close', {
          duration: 3000,
          horizontalPosition: 'end',
          verticalPosition: 'top'
        });
        this.dialogRef.close({
          action: 'archive',
          success: true,
          shouldRefresh: true
        } as OrganizationDetailsDialogResult);
      },
      error: (err) => {
        console.error('Error archiving organization:', err);
        this.snackBar.open('Failed to archive organization', 'Close', {
          duration: 5000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        this.processing = false;
        this.actionType = null;
      }
    });
  }



  cancelDelete() {
    this.showDeleteInput = false;
    this.deleteConfirmation = '';
  }

  confirmDelete() {
    if (this.deleteConfirmation !== 'DELETE') return;

    this.processing = true;
    this.actionType = 'delete';

    this.service.deleteOrganizationPermanently(this.data.organizationId).subscribe({
      next: () => {
        this.snackBar.open('Organization permanently deleted', 'Close', {
          duration: 3000,
          horizontalPosition: 'end',
          verticalPosition: 'top'
        });
        this.dialogRef.close({
          action: 'delete',
          success: true,
          shouldRefresh: true
        } as OrganizationDetailsDialogResult);
      },
      error: (err) => {
        console.error('Error deleting organization:', err);
        this.snackBar.open('Failed to delete organization', 'Close', {
          duration: 5000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        this.processing = false;
        this.actionType = null;
      }
    });
  }

  onReactivate() {
    this.processing = true;
    this.actionType = 'reactivate';

    this.service.reactivateOrganization(this.data.organizationId).subscribe({
      next: () => {
        this.snackBar.open('Organization reactivated successfully', 'Close', {
          duration: 3000,
          horizontalPosition: 'end',
          verticalPosition: 'top'
        });
        this.dialogRef.close({
          action: 'reactivate',
          success: true,
          shouldRefresh: true
        } as OrganizationDetailsDialogResult);
      },
      error: (err) => {
        console.error('Error reactivating organization:', err);
        this.snackBar.open('Failed to reactivate organization', 'Close', {
          duration: 5000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        this.processing = false;
        this.actionType = null;
      }
    });
  }

  onRestore() {
  this.processing = true;
  this.actionType = 'reactivate'; // reuse existing spinner state; no need for a new ActionType member unless you want one

  this.service.restoreDeletedOrganization(this.data.organizationId).subscribe({
    next: () => {
      this.snackBar.open('Organization restored successfully', 'Close', {
        duration: 3000,
        horizontalPosition: 'end',
        verticalPosition: 'top'
      });
      this.dialogRef.close({
        action: 'reactivate',
        success: true,
        shouldRefresh: true
      } as OrganizationDetailsDialogResult);
    },
    error: (err) => {
      console.error('Error restoring organization:', err);
      this.snackBar.open('Failed to restore organization', 'Close', {
        duration: 5000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
      this.processing = false;
      this.actionType = null;
    }
  });
}
  isArchived(): boolean {
    if (!this.orgData) return false;

    // Check if this is from the archived view (passed in data)
    if (this.data.isArchived) return true;

    // Check approval status if available (ApprovalStatus.Archived = 4)
    if (this.approvalData?.status === 4 || this.approvalData?.Status === 4) {
      return true;
    }

    // Check for archived extension in FHIR data
    const archivedExt = this.orgData?.extension?.find(
      (ext: any) => ext.url === 'http://elikita.com/fhir/StructureDefinition/organization-archived'
    );

    if (archivedExt) {
      const isArchived = archivedExt.valueBoolean === true || archivedExt.valueBoolean?.value === true;
      return isArchived;
    }

    return false;
  }
isDeleted(): boolean {
  if (this.approvalData?.status === 5 || this.approvalData?.Status === 5) {
    return true;
  }
  return false;
}

  /**
   * Check if organization can be deactivated/archived/deleted
   * Only active, non-archived organizations can be deactivated/archived/deleted
   */
canModify(): boolean {
  return this.isActive(this.orgData) && !this.isArchived() && !this.isDeleted() && !this.isParentOrganization();
}

canReactivate(): boolean {
  return (!this.isActive(this.orgData) || this.isArchived()) && !this.isDeleted() && !this.isParentOrganization();
}
  onClose() {
    this.dialogRef.close();
  }
}
